import { useDebounceEffect } from "ahooks";
import type { FunctionComponent } from "react";
import { useRef } from "react";
import type { PreloadedQuery } from "react-relay";
import { usePreloadedQuery } from "react-relay";

import { query } from "~/components/article/article-render/live-article";
import {
  getContainerId,
  getItemContainerId,
} from "~/components/article/article-render/live-article/helpers";
import {
  useFilteredLiveContents,
  useTimelineScrollEffect,
} from "~/components/article/article-render/live-article/hooks";
import type { liveArticleQuery } from "~/queries/__generated__/liveArticleQuery.graphql";

import { Container, StyledLiveArticleTimelineItem } from "./styles";

type Props = {
  className?: string;
  queryReference: PreloadedQuery<liveArticleQuery>;
};

export const LiveArticleTimeline: FunctionComponent<Props> = ({ className, queryReference }) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const data = usePreloadedQuery<liveArticleQuery>(query, queryReference);
  const { liveArticleScroller, scrollToElement } = useTimelineScrollEffect();
  const filteredLiveContents = useFilteredLiveContents(data.liveArticle);

  useDebounceEffect(
    () => {
      const type = "timeline";
      if (!liveArticleScroller.toScrollEntityId || liveArticleScroller.toScrollType !== type)
        return;
      scrollToElement(type, {
        containerId: getContainerId(type),
        duration: 500,
        smooth: true,
      });
    },
    [scrollToElement, liveArticleScroller],
    { wait: 800 },
  );

  return (
    <Container className={className} id={getContainerId("timeline")} ref={containerReference}>
      {filteredLiveContents.map(liveContent => (
        <>
          {liveContent && (
            <StyledLiveArticleTimelineItem
              id={getItemContainerId("timeline", liveContent.entityId ?? "")}
              key={liveContent.customTime}
              reference={liveContent}
            />
          )}
        </>
      ))}
    </Container>
  );
};

LiveArticleTimeline.displayName = "LiveArticleTimeline";
